import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { effectiveRole as resolveEffectiveRole, type AccountDto, type Role } from '../../shared/types/index.js';
import { ApiError, configureClient } from '../api/client.js';
import {
  fetchMe,
  impersonate as impersonateRequest,
  login as loginRequest,
  logout as logoutRequest,
} from '../api/auth.js';
import {
  isExpired,
  readCountry,
  readEditMode,
  readSession,
  writeCountry,
  writeEditMode,
  writeSession,
  type StoredSession,
} from './storage.js';

export type SessionStatus = 'booting' | 'anonymous' | 'authenticated';

export interface LoginOptions {
  /** Called as soon as the server accepted the credentials (before the session is activated). */
  onAuthenticated?: (account: AccountDto) => void;
  /** Delay before the session becomes active in React state, e.g. to play a success animation. */
  activateAfterMs?: number;
}

export interface SessionContextValue {
  status: SessionStatus;
  account: AccountDto | null;
  /** The admin behind the current session while impersonating, else null. */
  impersonatedBy: AccountDto | null;
  /** Selected data-scope country (ISO3), always one of `account.countries`. */
  country: string | undefined;
  /** While impersonating: act with operator rights on the account's data (true) or exactly as the account (false). */
  editMode: boolean;
  setEditMode: (on: boolean) => void;
  /** The role the UI and the server gate on; `viewer` when signed out. */
  effectiveRole: Role;
  login: (username: string, password: string, options?: LoginOptions) => Promise<AccountDto>;
  logout: () => Promise<void>;
  /** Admin only: act as another account until `stopImpersonation()`. */
  impersonate: (accountId: string) => Promise<AccountDto>;
  stopImpersonation: () => void;
  setCountry: (country: string) => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<StoredSession | null>(() => readSession());
  const [status, setStatus] = useState<SessionStatus>(() => (readSession() ? 'booting' : 'anonymous'));
  const [country, setCountryState] = useState<string | undefined>(() => {
    const stored = readSession();
    return stored ? readCountry(stored.account.countries) : undefined;
  });
  const [editMode, setEditModeState] = useState<boolean>(() => readEditMode());
  const sessionRef = useRef(session);
  const countryRef = useRef(country);
  const editModeRef = useRef(editMode);
  sessionRef.current = session;
  countryRef.current = country;
  editModeRef.current = editMode;

  const clear = useCallback(() => {
    writeSession(null);
    setSession(null);
    setStatus('anonymous');
  }, []);

  const apply = useCallback((next: StoredSession) => {
    writeSession(next);
    setSession(next);
    setStatus('authenticated');
    const selected = readCountry(next.account.countries);
    writeCountry(selected ?? null);
    setCountryState(selected);
  }, []);

  /** Ends an impersonation by restoring the admin's own session; a no-op otherwise. */
  const stopImpersonation = useCallback(() => {
    const original = sessionRef.current?.original;
    if (!original) return;
    if (isExpired(original)) clear();
    else apply(original);
  }, [apply, clear]);

  // The API client reads the live session through refs, so it never holds a stale token.
  useEffect(() => {
    configureClient({
      headers: () => {
        const current = sessionRef.current;
        if (!current) return {};
        const headers: Record<string, string> = { Authorization: `Bearer ${current.token}` };
        if (countryRef.current) headers['X-Country'] = countryRef.current;
        // The server assumes edit mode while impersonating; only the opt-out travels.
        if (current.impersonatedBy && !editModeRef.current) headers['X-Edit-Mode'] = '0';
        return headers;
      },
      // A rejected impersonation (e.g. the target was suspended) drops back to the admin, not to the login screen.
      onUnauthorized: () => (sessionRef.current?.original ? stopImpersonation() : clear()),
    });
  }, [clear, stopImpersonation]);

  // Revalidate a restored session once; an expired or revoked one drops back to the login screen.
  useEffect(() => {
    const restored = sessionRef.current;
    if (status !== 'booting' || !restored) return;
    let cancelled = false;
    fetchMe()
      .then(({ account, impersonatedBy }) => {
        if (!cancelled) apply({ ...restored, account, impersonatedBy });
      })
      .catch(err => {
        if (cancelled) return;
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          if (restored.original) stopImpersonation();
          else clear();
        } else {
          setStatus('authenticated'); // offline: keep the cached session, the dashboard shows its connection banner
        }
      });
    return () => {
      cancelled = true;
    };
  }, [status, apply, clear, stopImpersonation]);

  // Client-side expiry: log out when the token's lifetime ends.
  useEffect(() => {
    if (!session) return;
    const ms = Date.parse(session.expiresAt) - Date.now();
    if (!Number.isFinite(ms)) return;
    const expire = () => (sessionRef.current?.original ? stopImpersonation() : clear());
    if (ms <= 0 || isExpired(session)) {
      expire();
      return;
    }
    const timer = setTimeout(expire, Math.min(ms, 2 ** 31 - 1));
    return () => clearTimeout(timer);
  }, [session, clear, stopImpersonation]);

  const login = useCallback(
    async (username: string, password: string, { onAuthenticated, activateAfterMs = 0 }: LoginOptions = {}) => {
      const result = await loginRequest({ username, password });
      const next: StoredSession = { token: result.token, expiresAt: result.expiresAt, account: result.account };
      writeSession(next); // persisted at once, so a reload during the animation keeps the session
      onAuthenticated?.(result.account);
      if (activateAfterMs > 0) {
        await new Promise(resolve => setTimeout(resolve, activateAfterMs));
      }
      apply(next);
      return result.account;
    },
    [apply]
  );

  const logout = useCallback(async () => {
    if (sessionRef.current?.original) {
      stopImpersonation();
      return;
    }
    try {
      if (sessionRef.current) await logoutRequest();
    } catch {
      // The local session is dropped regardless.
    }
    clear();
  }, [clear, stopImpersonation]);

  const impersonate = useCallback(
    async (accountId: string) => {
      const current = sessionRef.current;
      if (!current) throw new Error('Not signed in');
      const result = await impersonateRequest(accountId);
      // Nested impersonation keeps the admin's own session as the one to return to.
      const original = current.original ?? current;
      apply({
        token: result.token,
        expiresAt: result.expiresAt,
        account: result.account,
        impersonatedBy: result.impersonatedBy,
        original,
      });
      return result.account;
    },
    [apply]
  );

  const setCountry = useCallback((next: string) => {
    const allowed = sessionRef.current?.account.countries ?? [];
    if (!allowed.includes(next)) return;
    writeCountry(next);
    setCountryState(next);
  }, []);

  const setEditMode = useCallback((on: boolean) => {
    writeEditMode(on);
    setEditModeState(on);
  }, []);

  const value = useMemo<SessionContextValue>(() => {
    const account = status === 'authenticated' ? (session?.account ?? null) : null;
    const impersonatedBy = status === 'authenticated' ? (session?.impersonatedBy ?? null) : null;
    return {
      status,
      account,
      impersonatedBy,
      country,
      editMode,
      setEditMode,
      effectiveRole: account ? resolveEffectiveRole(account.role, impersonatedBy?.role, editMode) : 'viewer',
      login,
      logout,
      impersonate,
      stopImpersonation,
      setCountry,
    };
  }, [status, session, country, editMode, setEditMode, login, logout, impersonate, stopImpersonation, setCountry]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used within SessionProvider');
  return context;
}
